/**
 * CONF-93 network port arithmetic.
 *
 * Ports per NIC come from the card (registry data). NIC counts per node come
 * from the CCI bundle shape: a single-port option puts two cards in a director
 * (the "2x SP" bundles, D1N-002 / D1N-005); a dual-port option puts one
 * (D1N-001 / D1N-003). Hybrid storage nodes always take OCP + PCIe; flash
 * nodes take what their model says (CONF-210: the Dual CPU node takes
 * 2 PCIe + 1 OCP), read from the same resolver the BOM uses.
 */
import { resolveFlashNodeModel } from './flashNodeModel';

// Matches the app's default card (useConfigurationLogic) and the legacy
// 'Ethernet' migration target, so "could not resolve" and "not specified"
// land on the same option rather than three different ones.
const DEFAULT_OPTION_ID = '200GbE';
const STORAGE_NIC_COUNT = 2; // OCP + PCIe, every option (hybrid S-nodes)

const flashNicCount = (config) => {
    const { pcie, ocp } = resolveFlashNodeModel(config).nicLayout;
    return pcie + ocp;
};

/**
 * Look up a network option by id, falling back to the default rather than
 * throwing — a config loaded from an older quote may name something unknown.
 */
export const getNetworkOption = (capabilities, id) => {
    const options = capabilities?.networkOptions ?? [];
    return options.find(o => o.id === id)
        || options.find(o => o.id === DEFAULT_OPTION_ID)
        || options[0];
};

export const calculateNetworkPorts = (config, capabilities) => {
    const option = getNetworkOption(capabilities, config.interfaceOption);
    const portsPerNic = option?.portsPerNic ?? 2;
    const directorNicCount = option?.directorNicCards === 'OCP+PCIe' ? 2 : 1;

    const portsPerDirector = directorNicCount * portsPerNic;
    const portsPerStorageNode = STORAGE_NIC_COUNT * portsPerNic;
    const portsPerFlashNode = flashNicCount(config) * portsPerNic;

    const directorPorts = (config.veloCount || 0) * portsPerDirector;
    const hybridPorts = config.hybridEnabled ? (config.hybridNodeCount || 0) * portsPerStorageNode : 0;
    const afaPorts = config.afaEnabled ? (config.afaNodeCount || 0) * portsPerFlashNode : 0;

    return {
        portsPerDirector,
        portsPerStorageNode,
        portsPerFlashNode,
        directorPorts,
        hybridPorts,
        afaPorts,
        totalPorts: directorPorts + hybridPorts + afaPorts,
    };
};
